import React, { useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import moment from 'moment'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { IShipmentStates, ITrailerStatesEnum } from 'types/graphqlTypes'
import { FORMATS } from 'constants/datetime'
import { useTrailerQuery } from '../graphql/Trailer'
import { AddTrailerForm, IStatusOption, trailerStatusToStatusOption } from './AddTrailerForm'
import { TAddTrailerSchema } from './AddTrailerSchema'
import { useCarrierApplicationInfoQuery } from './graphql/CarrierApplicationInfo'
import { useUpdateTrailerMutation } from './graphql/UpdateTrailerMutation'
import { useAddTrailerForm } from './useAddTrailerForm'

export interface IEditTrailerProps extends Omit<IModalBaseProps, 'modalType'> {
  shipperApplicationId?: string
  carrierApplicationId?: string
  trailerId: string
  onSuccess?: () => void
}

const STATUS_OPTIONS_BY_STATE: Record<
  ITrailerStatesEnum | 'default',
  (state?: IShipmentStates) => IStatusOption[]
> = {
  assigned_to_facility: () =>
    [
      ITrailerStatesEnum.assigned_to_facility,
      ITrailerStatesEnum.assigned_to_shipment,
      ITrailerStatesEnum.inactive,
      ITrailerStatesEnum.unassigned,
    ].map(trailerStatusToStatusOption),
  assigned_to_shipment: state => {
    const isDelivered =
      state &&
      [
        IShipmentStates.delivered,
        IShipmentStates.documents_received,
        IShipmentStates.invoice_sent,
      ].includes(state)

    if (isDelivered) {
      return [
        ITrailerStatesEnum.assigned_to_facility,
        ITrailerStatesEnum.assigned_to_shipment,
        ITrailerStatesEnum.inactive,
        ITrailerStatesEnum.unassigned,
      ].map(trailerStatusToStatusOption)
    }
    return [ITrailerStatesEnum.assigned_to_shipment].map(trailerStatusToStatusOption)
  },
  empty_at_delivery: () =>
    [
      ITrailerStatesEnum.empty_at_delivery,
      ITrailerStatesEnum.assigned_to_shipment,
      ITrailerStatesEnum.inactive,
      ITrailerStatesEnum.unassigned,
    ].map(trailerStatusToStatusOption),
  inactive: () =>
    [
      ITrailerStatesEnum.assigned_to_facility,
      ITrailerStatesEnum.assigned_to_shipment,
      ITrailerStatesEnum.inactive,
      ITrailerStatesEnum.unassigned,
    ].map(trailerStatusToStatusOption),
  unassigned: () =>
    [
      ITrailerStatesEnum.assigned_to_facility,
      ITrailerStatesEnum.assigned_to_shipment,
      ITrailerStatesEnum.inactive,
      ITrailerStatesEnum.unassigned,
    ].map(trailerStatusToStatusOption),
  default: () => [],
}

export const EditTrailer = ({
  carrierApplicationId,
  onSuccess = () => {},
  onClose,
  open,
  shipperApplicationId,
  trailerId,
  ...props
}: IEditTrailerProps) => {
  const { data } = useTrailerQuery({ variables: { trailerId } })

  const { trailer } = data || {}
  const { arrivedAt, carrier, note, number, shipment, shipperFacility, state } = trailer || {}
  const { id: carrierId } = carrier || {}
  const { facility, uuid: facilityUuid } = shipperFacility || {}
  const { location } = facility || {}
  const { timezone } = location || {}
  const { id: shipmentId } = shipment || {}

  const { openToast } = useGlobalToast()

  const methods = useAddTrailerForm({
    defaultValues: {
      status: state,
      trailerNotes: note || '',
      trailerNumber: number,
      carrier: carrierId || '',
      shipment: shipmentId || '',
      facility: facilityUuid,
      arrivedAt: {
        date: moment(arrivedAt)
          .tz(timezone || FORMATS.defaultTimeZone)
          .format(FORMATS.iso8601Format),
        time: moment(arrivedAt)
          .tz(timezone || FORMATS.defaultTimeZone)
          .format(FORMATS.militaryTimeFormat),
      },
    },
  })

  const { data: carrierApplicationQueryResult } = useCarrierApplicationInfoQuery({
    variables: {
      carrierId: carrierApplicationId || '',
    },
    skip: !carrierApplicationId,
  })
  const { carrierApplication: carrierApplicationProp } = carrierApplicationQueryResult || {}

  const [updateTrailer] = useUpdateTrailerMutation()

  const { handleSubmit, reset } = methods || {}

  const [mutationErrors, setMutationErrors] = useState<string | null>(null)

  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: () => {
        onClose()
      },
      type: 'dismiss',
      dataTest: 'cancel-button',
    },
    {
      label: 'Save',
      action: handleSubmit(async (formData: TAddTrailerSchema) => {
        const {
          arrivedAt,
          carrier: carrierId,
          facility: facilityUuid,
          shipment: shipmentId,
          status,
          trailerNotes: note,
          trailerNumber,
        } = formData || {}
        const shipperFacilityUuid = facilityUuid || null
        const facilityArrivedAtDate = arrivedAt.date
        const facilityArrivedAtTime = arrivedAt.time

        setMutationErrors(null)

        try {
          const { data, errors } = await updateTrailer({
            variables: {
              input: {
                id: trailerId || '',
                shipmentId,
                carrierId,
                number: trailerNumber,
                note,
                // if shipper facility is null BE is going to setup the status for the trailer as unassigned
                shipperFacilityUuid: ['inactive', 'unassigned'].includes(status || '')
                  ? null
                  : shipperFacilityUuid,
                // BE only accepts inactive state when editing a trailer. It will handle the rest of the state logic.
                ...(['inactive', 'unassigned'].includes(status || '') && {
                  state: status as ITrailerStatesEnum,
                }),
                facilityArrivedAtDate,
                facilityArrivedAtTime,
              },
            },
          })
          const { updateTrailer: response } = data || {}
          const { errors: responseErrors } = response || {}
          if (errors && errors.length > 0) {
            setMutationErrors(errors.map(e => e.message).join('; '))
          } else if (responseErrors && responseErrors.length > 0) {
            setMutationErrors(responseErrors.map(e => e.message).join('; '))
          } else {
            onSuccess()
            openToast({
              toastType: 'default',
              toastMessage: 'Trailer successfully modified.',
            })
            onClose()
          }
        } catch (e) {
          setMutationErrors('There was an error adding this trailer')
        }
      }),
      type: 'confirm',
      dataTest: 'confirm-button',
    },
  ]

  useEffect(() => {
    reset({
      status: state,
      trailerNotes: note || '',
      trailerNumber: number,
      carrier: carrierId || '',
      shipment: shipmentId || '',
      facility: facilityUuid,
      arrivedAt: {
        date: arrivedAt
          ? moment(arrivedAt)
              .tz(timezone || FORMATS.defaultTimeZone)
              .format(FORMATS.iso8601Format)
          : undefined,
        time: arrivedAt
          ? moment(arrivedAt)
              .tz(timezone || FORMATS.defaultTimeZone)
              .format(FORMATS.militaryTimeFormat)
          : undefined,
      },
    })
  }, [state, note, number, carrierId, shipmentId, facilityUuid, arrivedAt, reset, timezone])

  return (
    <ModalBase
      dataTest='EditTrailer'
      modalType={MODAL_TYPES.form}
      title='Edit Trailer'
      size={4}
      open={open}
      actions={actions}
      headerClassName='pb0'
      onClose={onClose}
      {...props}>
      <FormProvider {...methods}>
        <AddTrailerForm
          carrierApplication={carrierApplicationProp || carrier || undefined}
          shipment={shipment || undefined}
          shipperFacility={shipperFacility || undefined}
          mutationErrors={mutationErrors}
          shipperApplicationId={shipperApplicationId}
          showEmptyAtDelivery
          shipmentDisabled={state === 'assigned_to_shipment'}
          displayStatusOptionsGenerator={() =>
            STATUS_OPTIONS_BY_STATE[(state as ITrailerStatesEnum) || 'default'](shipment?.state) ||
            []
          }
        />
      </FormProvider>
    </ModalBase>
  )
}
